import {isValidHttpUrl} from "./url";
import { auth } from "../firebase";

/**
 * Wywołuje lokalny backend Express do rozpoznawania produktów ze zdjęcia
 * Backend automatycznie sprawdza limit IP i użytkownika (zweryfikowanego przez JWT)
 *
 * @param {string} imageData - Data URL lub URL obrazu (blob:, data:, http/https)
 * @param {string} provider - Dostawca API ('openai' lub 'clarifai')
 * @returns {Promise<{items: string[], remaining: number, limit: number}>}
 */
export async function recognizePhotoViaBackend(imageData, provider = 'clarifai', lang = 'en') {
    try {
        const url = (imageData || "").trim();
        if (!url) {
            throw new Error("Podaj adres URL zdjęcia.");
        }

        if (!auth.currentUser) {
            throw new Error("Musisz być zalogowany, aby korzystać z rozpoznawania zdjęć.");
        }

        const token = await auth.currentUser.getIdToken();

        // Helper: przygotuj payload obrazu dla Clarifai
        let imagePayload = await getImagePayload(url);

        if (provider === 'openai') {
            // Konwertuj blob: URL do data URL jeśli potrzeba
            let preparedImageData;

            if (imageData.startsWith('blob:')) {
                const response = await fetch(imageData);
                const blob = await response.blob();
                preparedImageData = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                imagePayload = preparedImageData;
            }
        }

        // URL backendu - zmień po wdrożeniu na serwer
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

        // ✅ Wywołaj backend z tokenem JWT (nie userId!)
        const response = await fetch(`${BACKEND_URL}/api/recognize-photo`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                imageData: imagePayload,
                provider: provider,
                lang: lang
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            // Backend zwrócił błąd
            throw new Error(data.error || 'Błąd podczas rozpoznawania zdjęcia');
        }

        return {
            items: data.items || [],
            remaining: data.userRemaining || 0,
            limit: data.userLimit || 3
        };

    } catch (error) {
        console.error('Error calling backend recognize-photo:', error);

        // Parsuj różne typy błędów
        if (error.message.includes('limit')) {
            throw new Error(error.message);
        } else if (error.message.includes('Failed to fetch')) {
            throw new Error('Nie można połączyć się z serwerem. Sprawdź czy backend działa.');
        } else {
            throw new Error(error.message || 'Wystąpił błąd podczas rozpoznawania zdjęcia.');
        }
    }
}

async function getImagePayload (src) {
    if (!src) throw new Error("Brak źródła obrazu.");

    // Zdjęcie z galerii/kamery: blob: URL -> konwersja do base64
    if (src.startsWith("blob:")) {
        const resp = await fetch(src);
        const blob = await resp.blob();
        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result || "";
                const b64 = String(result).split(",")[1] || "";
                resolve(b64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
        return { base64 };
    }

    // Jeśli mamy już data: URL, wyciągnij samą część base64
    if (src.startsWith("data:")) {
        const b64 = src.split(",")[1] || "";
        return { base64: b64 };
    }

    // Dla publicznych http/https nadal można używać URL
    if (isValidHttpUrl(src)) {
        return { url: src };
    }

    throw new Error("Nieprawidłowe źródło obrazu.");
}

/**
 * Sprawdza pozostałe użycia dla zalogowanego użytkownika
 * @returns {Promise<{remaining: number, limit: number, used: number}>}
 */
export async function checkLimit() {
    try {
        if (!auth.currentUser) {
            return { remaining: 0, limit: 3, used: 0 };
        }

        const token = await auth.currentUser.getIdToken();

        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

        const response = await fetch(`${BACKEND_URL}/api/check-limit`, {
            headers: {
                'Authorization': `Bearer ${token}`  // ✅ JWT token
            }
        });
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Błąd podczas sprawdzania limitu');
        }

        return data;
    } catch (error) {
        console.error('Error checking limit:', error);
        return { remaining: 0, limit: 3, used: 0 };
    }
}